// src/features/sheets/components/SpreadsheetGrid.tsx
// Grid component for spreadsheet cells

import React, { forwardRef, useRef, useImperativeHandle } from "react";
import { Zap } from "lucide-react";
import type { CellPosition, CellFormat, FillDragState } from "../types";
import { DEFAULT_COL_WIDTH } from "../types";
import { colIndexToLetter, containsLiveFormula, isNumericValue } from "../utils/formulaEngine";

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

export interface SpreadsheetGridProps {
  data: string[][];
  formats: Record<string, CellFormat>;
  colWidths: number[];
  rows: number;
  cols: number;
  selectedCell: CellPosition | null;
  editingCell: CellPosition | null;
  editValue: string;
  fillPreviewCells: CellPosition[];
  fillDrag: FillDragState | null;
  liveCache: Map<string, number | null>;
  onCellClick: (row: number, col: number) => void;
  onCellDoubleClick: (row: number, col: number) => void;
  onCellMouseEnter: (row: number, col: number) => void;
  onEditChange: (value: string) => void;
  onEditBlur: () => void;
  onEditKeyDown: (e: React.KeyboardEvent) => void;
  onFillDragStart: (e: React.MouseEvent, cell: CellPosition) => void;
  onResizeStart: (e: React.MouseEvent, col: number) => void;
  getCellDisplay: (row: number, col: number) => string;
  readOnly?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export const SpreadsheetGrid = forwardRef<HTMLDivElement, SpreadsheetGridProps>(
  (
    {
      data,
      formats,
      colWidths,
      rows,
      cols,
      selectedCell,
      editingCell,
      editValue,
      fillPreviewCells,
      fillDrag,
      onCellClick,
      onCellDoubleClick,
      onCellMouseEnter,
      onEditChange,
      onEditBlur,
      onEditKeyDown,
      onFillDragStart,
      onResizeStart,
      getCellDisplay,
      readOnly = false,
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => containerRef.current!, []);

    // Focus input when editing starts
    React.useEffect(() => {
      if (editingCell && inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, [editingCell]);

    return (
      <div
        ref={containerRef}
        className="flex-1 min-h-0 overflow-auto focus:outline-none"
        tabIndex={-1}
      >
        <table
          className="border-collapse text-xs"
          style={{
            tableLayout: "fixed",
            width: colWidths.reduce((a, b) => a + b, 40) // 40px for row number column
          }}
        >
          {/* Column widths definition - required for table-layout: fixed */}
          <colgroup>
            <col style={{ width: 40 }} /> {/* Row number column */}
            {colWidths.map((width, i) => (
              <col key={i} style={{ width: width || DEFAULT_COL_WIDTH }} />
            ))}
          </colgroup>

          {/* Header */}
          <thead className="sticky top-0 z-20">
            <tr>
              {/* Row number header */}
              <th className="border border-slate-700/50 bg-slate-800/95 p-1 text-[10px] text-slate-500 font-medium sticky left-0 z-30">
                #
              </th>
              {/* Column headers */}
              {Array.from({ length: cols }, (_, i) => (
                <th
                  key={i}
                  className="border border-slate-700/50 bg-slate-800/95 p-1 text-[10px] text-slate-400 font-medium relative group"
                >
                  {colIndexToLetter(i)}
                  {/* Resize handle */}
                  <div
                    className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-500/50 group-hover:bg-slate-600"
                    onMouseDown={(e) => onResizeStart(e, i)}
                  />
                </th>
              ))}
            </tr>
          </thead>

          {/* Body */}
          <tbody>
            {Array.from({ length: rows }, (_, rowIdx) => (
              <tr key={rowIdx}>
                {/* Row number */}
                <td className="border border-slate-700/50 bg-slate-800/80 p-1 text-center text-[10px] text-slate-500 font-medium sticky left-0 z-10">
                  {rowIdx + 1}
                </td>
                {/* Cells */}
                {Array.from({ length: cols }, (_, colIdx) => {
                  const isSelected =
                    selectedCell?.row === rowIdx && selectedCell?.col === colIdx;
                  const isEditing =
                    editingCell?.row === rowIdx && editingCell?.col === colIdx;
                  const cellValue = data[rowIdx]?.[colIdx] || "";
                  const displayValue = getCellDisplay(rowIdx, colIdx);
                  const isLive = containsLiveFormula(cellValue);
                  const format = formats[`${rowIdx},${colIdx}`];
                  const isNum =
                    isNumericValue(displayValue) || !isNaN(parseFloat(displayValue));
                  const isFillPreview = fillPreviewCells.some(
                    (c) => c.row === rowIdx && c.col === colIdx
                  );

                  const alignClass =
                    format?.align === "center"
                      ? "text-center"
                      : format?.align === "right"
                      ? "text-right"
                      : isNum && !format?.align
                      ? "text-right"
                      : "text-left";

                  return (
                    <td
                      key={colIdx}
                      className={`border border-slate-700/50 p-0 relative overflow-hidden ${
                        isSelected
                          ? "ring-2 ring-blue-500 ring-inset bg-blue-500/10"
                          : isFillPreview
                          ? "bg-blue-500/20 ring-1 ring-blue-400/50 ring-inset"
                          : "hover:bg-slate-700/30"
                      } ${isLive ? "bg-emerald-500/5" : ""}`}
                      onClick={() => onCellClick(rowIdx, colIdx)}
                      onDoubleClick={() => onCellDoubleClick(rowIdx, colIdx)}
                      onMouseEnter={() => {
                        if (fillDrag) {
                          onCellMouseEnter(rowIdx, colIdx);
                        }
                      }}
                    >
                      {isEditing ? (
                        <input
                          ref={inputRef}
                          type="text"
                          value={editValue}
                          onChange={(e) => onEditChange(e.target.value)}
                          onBlur={onEditBlur}
                          onKeyDown={onEditKeyDown}
                          className="w-full h-full px-1.5 py-1 bg-white/10 text-slate-200 focus:outline-none font-mono text-xs"
                        />
                      ) : (
                        <div
                          className={`px-1.5 py-1 truncate min-h-[24px] tabular-nums ${alignClass} ${
                            displayValue.startsWith?.("#")
                              ? "text-red-400"
                              : displayValue === "..."
                              ? "text-slate-500 italic"
                              : isLive
                              ? "text-emerald-300"
                              : "text-slate-200"
                          } ${format?.bold ? "font-semibold" : ""}`}
                        >
                          {isLive && (
                            <Zap className="w-2.5 h-2.5 inline-block mr-0.5 text-emerald-400 -mt-0.5" />
                          )}
                          {displayValue}
                        </div>
                      )}
                      {/* Fill handle */}
                      {isSelected && !isEditing && !readOnly && (
                        <div
                          className="absolute -bottom-[3px] -right-[3px] w-[7px] h-[7px] bg-blue-500 border border-slate-900 cursor-crosshair z-10 hover:bg-blue-400"
                          onMouseDown={(e) =>
                            onFillDragStart(e, { row: rowIdx, col: colIdx })
                          }
                          title="Drag to fill"
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
);

SpreadsheetGrid.displayName = "SpreadsheetGrid";

export default SpreadsheetGrid;
